import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, ArrowUpCircle } from 'lucide-react';

/**
 * Existing Balance Warning popup (Iter 142.10)
 * ============================================
 * Fires when a player taps "Load" on a game card that ALREADY has a
 * non-trivial provider balance ($0.20 threshold). Forces them to either
 * cash out the existing balance first (one click) or explicitly cancel.
 *
 * Why: Loading on top of an unredeemed balance dilutes the multiplier
 * rule and is the #1 source of "I can't cash out" support tickets.
 *
 * Props:
 *   game        — game info {id, name}
 *   balance     — Number, current provider balance
 *   processing  — bool, true while the redeem is in flight
 *   onRedeem    — () => void, parent handles the actual redeem call
 *   onCancel    — () => void, close the popup without action
 */
export default function ExistingBalanceWarning({
  game,
  balance,
  processing,
  onRedeem,
  onCancel,
}) {
  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !processing) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, processing]);

  const overlay = (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={() => { if (!processing) onCancel?.(); }}
      data-testid="existing-balance-overlay"
    >
      <div
        className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl"
        style={{
          background: 'linear-gradient(180deg, #1a1a2e 0%, #0B0E14 100%)',
          border: '1px solid rgba(245,158,11,0.35)',
          boxShadow: '0 -10px 40px rgba(245,158,11,0.2), 0 20px 60px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="existing-balance-popup"
      >
        {/* Close X */}
        {!processing && (
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition"
            data-testid="existing-balance-close"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        )}

        <div className="px-6 pt-8 pb-6">
          {/* Warning icon */}
          <div
            className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(245,158,11,0.25), rgba(245,158,11,0.1))',
              border: '1px solid rgba(245,158,11,0.4)',
            }}
          >
            <AlertTriangle className="w-8 h-8 text-amber-400" />
          </div>

          <h2
            className="text-2xl font-black text-white text-center leading-tight"
            data-testid="existing-balance-title"
          >
            You still have a balance
          </h2>
          <p className="text-sm text-amber-300/80 text-center mt-1 mb-5">
            in {game?.name || 'this game'}
          </p>

          {/* Big balance display */}
          <div
            className="rounded-2xl p-5 mb-5 text-center"
            style={{
              background: 'rgba(34,197,94,0.08)',
              border: '1px solid rgba(34,197,94,0.25)',
            }}
            data-testid="existing-balance-amount-card"
          >
            <p className="text-[11px] uppercase tracking-wide text-emerald-300/70 font-semibold mb-1">
              Current Game Balance
            </p>
            <p
              className="text-4xl sm:text-5xl font-black text-emerald-300 tabular-nums"
              data-testid="existing-balance-amount"
            >
              ${(Number(balance) || 0).toFixed(2)}
            </p>
          </div>

          <p className="text-base text-gray-300 leading-relaxed text-center mb-6">
            Please cash out this balance first.{' '}
            <span className="text-white font-semibold">
              Loading on top of it would dilute the multiplier rule.
            </span>
          </p>

          {/* Redeem button — primary CTA */}
          <button
            onClick={onRedeem}
            disabled={processing}
            className="w-full flex items-center justify-center gap-2.5 py-4 rounded-2xl text-base font-bold transition-all disabled:opacity-60 mb-3"
            style={{
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              color: '#fff',
              border: '2px solid rgba(34,197,94,0.5)',
              boxShadow: '0 8px 24px rgba(34,197,94,0.25)',
            }}
            data-testid="existing-balance-redeem-btn"
          >
            {processing ? (
              <div className="spinner w-5 h-5" />
            ) : (
              <>
                <ArrowUpCircle className="w-5 h-5" />
                Cash Out ${(Number(balance) || 0).toFixed(2)}
              </>
            )}
          </button>

          <button
            onClick={onCancel}
            disabled={processing}
            className="w-full py-3 rounded-2xl text-sm font-semibold text-gray-400 hover:text-white hover:bg-white/5 transition disabled:opacity-50"
            data-testid="existing-balance-cancel-btn"
          >
            Cancel
          </button>

          <p className="text-[11px] text-gray-500 text-center mt-3 leading-relaxed">
            Tip: cashed-out funds return to the same bucket you loaded with —
            FP→FP, cash→wallet.
          </p>
        </div>
      </div>
    </div>
  );

  const host = typeof document !== 'undefined' ? document.body : null;
  return host ? createPortal(overlay, host) : overlay;
}
